/**
 * i18n — static UI string catalog for the Chula Control Tower.
 *
 * Covers the ~30 core labels that appear in panels, status messages, and
 * common controls. Not a full translation system — just enough scaffolding
 * so future translators have a single file to edit and no string is hardcoded
 * in more than one place.
 *
 * Usage:
 *   import { uiStrings, type Locale } from "@chula/shared";
 *   const s = uiStrings("th");
 *   // s.loading === "กำลังโหลด…"
 *
 * For trilingual text attached to *data* objects (headlines, source labels,
 * descriptions), use the existing `translate(locale, trilingualText)` utility
 * from locale.ts instead.
 */

import type { Locale } from "./types";

export interface UiStrings {
  // Generic states
  loading: string;
  unavailable: string;
  error: string;
  refreshing: string;
  noData: string;

  // Timestamps
  updatedAt: string;    // prefix, e.g. "Updated"
  minutesAgo: string;   // suffix, e.g. "m ago"

  // Common controls
  refresh: string;
  close: string;
  search: string;
  addClock: string;
  removeClock: string;

  // Panel headers (abbrev — used as eyebrow labels)
  intelFeed: string;
  markets: string;
  executive: string;
  energy: string;
  weather: string;
  aiQuality: string;
  incidents: string;
  transit: string;

  // Search
  searchBuildings: string;
  searchBuildingsPlaceholder: string;

  // Chat
  chatPlaceholder: string;

  // Fallback tiers
  tierLive: string;
  tierCache: string;
  tierScenario: string;
}

const EN: UiStrings = {
  loading: "Loading…",
  unavailable: "Unavailable",
  error: "Error",
  refreshing: "Refreshing…",
  noData: "No data",

  updatedAt: "Updated",
  minutesAgo: "m ago",

  refresh: "Refresh",
  close: "Close",
  search: "Search",
  addClock: "Add clock",
  removeClock: "Remove clock",

  intelFeed: "INTEL FEED",
  markets: "MARKETS",
  executive: "Executive Brief",
  energy: "Energy · campus",
  weather: "Weather",
  aiQuality: "Air Quality",
  incidents: "Incidents",
  transit: "Transit",

  searchBuildings: "Search campus buildings",
  searchBuildingsPlaceholder: 'Search buildings — try "engineering" or "วิศวกรรม"',

  chatPlaceholder: "Ask about Chula, Bangkok, campus data…",

  tierLive: "Live",
  tierCache: "Cached",
  tierScenario: "Demo data",
};

const TH: UiStrings = {
  loading: "กำลังโหลด…",
  unavailable: "ไม่พร้อมใช้งาน",
  error: "เกิดข้อผิดพลาด",
  refreshing: "กำลังรีเฟรช…",
  noData: "ไม่มีข้อมูล",

  updatedAt: "อัปเดต",
  minutesAgo: "น. ที่แล้ว",

  refresh: "รีเฟรช",
  close: "ปิด",
  search: "ค้นหา",
  addClock: "เพิ่มนาฬิกา",
  removeClock: "ลบนาฬิกา",

  intelFeed: "ข่าวสาร",
  markets: "ตลาด",
  executive: "สรุปผู้บริหาร",
  energy: "พลังงาน · วิทยาเขต",
  weather: "สภาพอากาศ",
  aiQuality: "คุณภาพอากาศ",
  incidents: "เหตุการณ์",
  transit: "ระบบขนส่ง",

  searchBuildings: "ค้นหาอาคารในวิทยาเขต",
  searchBuildingsPlaceholder: 'ค้นหาอาคาร — ลอง "วิศวกรรม" หรือ "engineering"',

  chatPlaceholder: "ถามเกี่ยวกับจุฬาฯ กรุงเทพฯ หรือข้อมูลวิทยาเขต…",

  tierLive: "สด",
  tierCache: "แคช",
  tierScenario: "ข้อมูลตัวอย่าง",
};

const STRINGS: Record<Locale, UiStrings> = {
  en: EN,
  th: TH,
  zh: EN, // Chinese strings TODO — fall back to English until translated
};

/** Return the UI string catalog for the given locale. */
export function uiStrings(locale: Locale): UiStrings {
  return STRINGS[locale] ?? EN;
}
